import { IBaseViews } from "app/based/baseViews/BaseViews";
import { DependenciesInjection } from "app/di/DependencyInjection";
import LoadingScreenComponent from "components/generalComponents/loadingScreenComponent/LoadingScreenComponent";
import { higherOrderComponent } from "components/higherOrderComponent/HigherOrderComponent";
import PrivateRoute from "components/layoutComponent/PrivateRoute";
import { FeaturedSideBarModel } from "components/sidebar/data/models/SidebarMenuConfigModel";
import { AuthenticationContext } from "context/AuthenticationContext";
import { focusHandling } from "cruip-js-toolkit";
import GeneralFeature from "features/generals/presentation/views/GeneralFeature";
import { AuthenticationToken } from "features/login/di/AuthenticationToken";
import LoginFeature from "features/login/presentation/views/LoginFeature";
import NotFound from "features/notFound/NotFound";
import AppPages from "navigations/AppPages";
import React, {
  createElement,
  ReactNode,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { DataFormat } from "utils/format/DataFormat";

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const routeParams = useParams();
  const [diInjected, setDiInjected] = useState<boolean>(false);
  const { menuConfig } = AuthenticationContext();

  const initDependency = async () => {
    try {
      await DependenciesInjection.initInjections();
      setDiInjected(true);
    } catch (error) {
      console.error("Dependency Injection error:", error);
    }
  };

  const mapMenuToRoutes = (menu: FeaturedSideBarModel) => {
    const Component = AppPages.getPage(menu.componentName);
    const diToken = AppPages.getToken(menu.componentName);
    const props = AppPages.getAddtionalProps(menu.componentName);
    const menuKey = `${menu?.name} - ${menu?.componentName} - ${menu?.path}`;

    if (!Component) return <LoadingScreenComponent />;

    return (
      <React.Fragment key={`${menuKey} - routes`}>
        <Route
          path={menu.path}
          key={menuKey}
          element={
            <PrivateRoute path={menu.path} key={menuKey}>
              {!diInjected ? null : (
                <Suspense fallback={<LoadingScreenComponent />}>
                  {createElement(
                    higherOrderComponent(Component, {
                      token: diToken || "",
                      addtionalProps: {
                        ...(props as unknown as IBaseViews),
                        routeParams,
                      },
                    }),
                  )}
                </Suspense>
              )}
            </PrivateRoute>
          }
        />
        <Route
          path={`${menu?.path}/:searchCode`}
          key={`${menuKey} - searchCode`}
          element={
            <PrivateRoute
              path={`${menu?.path}/:searchCode`}
              key={`${menuKey} - searchCode`}
            >
              {!diInjected ? null : (
                <Suspense fallback={<LoadingScreenComponent />}>
                  {createElement(
                    higherOrderComponent(Component, {
                      token: diToken || "",
                      addtionalProps: {
                        ...(props as unknown as IBaseViews),
                        routeParams,
                      },
                    }),
                  )}
                </Suspense>
              )}
            </PrivateRoute>
          }
        />
      </React.Fragment>
    );
  };

  const hasMenuPath = (
    menus: FeaturedSideBarModel[] = [],
    pathname?: string,
  ): boolean => {
    if (!pathname) {
      return false;
    }

    for (const menu of menus) {
      if (menu?.path === pathname) {
        return true;
      }

      if (menu?.sub?.length && hasMenuPath(menu.sub, pathname)) {
        return true;
      }
    }

    return false;
  };

  const routeRendering = useMemo(() => {
    let routes: Array<ReactNode> = [];
    menuConfig?.menu?.forEach((menu) => {
      routes.push(mapMenuToRoutes(menu));
      if (menu.sub?.length > 0) {
        menu.sub?.forEach((subMenu) => {
          routes.push(mapMenuToRoutes(subMenu));
          if (subMenu.path === "/transaction-history/app-sim-mobifone") {
          }
        });
      }
    });
    return routes;
  }, [menuConfig]);

  const pageRendering = () => {
    const isNotFound =
      !!DataFormat.getToken() &&
      location?.pathname !== "/" &&
      !!menuConfig &&
      !hasMenuPath(menuConfig?.menu || [], location?.pathname);

    if (typeof window === "undefined" || !diInjected) {
      return <>{<LoadingScreenComponent />}</>;
    }

    return (
      <>
        <Route
          path={"/"}
          key={"default"}
          element={
            <PrivateRoute path={"/"} key={"default"}>
              <GeneralFeature />
            </PrivateRoute>
          }
        />
        {routeRendering}
        {isNotFound ? (
          <Route
            path={location?.pathname}
            key={"not-found-ui"}
            element={
              <PrivateRoute path={location?.pathname} key={"not-found-ui"}>
                <NotFound />
              </PrivateRoute>
            }
          />
        ) : (
          <></>
        )}
      </>
    );
  };

  useEffect(() => {
    if (!diInjected) {
      initDependency();
    }
  }, []);

  useEffect(() => {
    document.querySelector("html").style.scrollBehavior = "auto";
    window.scroll({ top: 0 });
    document.querySelector("html").style.scrollBehavior = "";
    focusHandling(null);
  }, [location.pathname]);

  if (!diInjected) {
    return <LoadingScreenComponent />;
  }

  return (
    <Routes>
      <Route
        path="/login"
        key={"login-route"}
        element={
          <LoginFeature
            navigate={navigate}
            token={AuthenticationToken.LoginViewModel}
          />
        }
      ></Route>
      <>{pageRendering()}</>
      <Route
        path="/404"
        element={
          <PrivateRoute path="/404">
            <NotFound />
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

export default App;
